// src/screens/ServicesScreen.jsx

import React, { useEffect, useId, useRef, useState } from "react";
import { X, ArrowUpRight, ArrowLeft, Check, Loader2 } from "lucide-react";
import { app } from "../../src/Firebase-config";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";

const db = getFirestore(app);

/* ======================================================
   IDENTIDAD VISUAL
====================================================== */

const DISPLAY_FONT = {
  fontFamily: '"Arial Narrow", "Roboto Condensed", Arial, sans-serif',
  fontStretch: "condensed",
};

const GRID_BACKGROUND = {
  backgroundImage: `
    linear-gradient(rgba(255,255,255,.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.06) 1px, transparent 1px)
  `,
  backgroundSize: "64px 64px",
};


/* ======================================================
   RESPONSIVO — MISMA ESCALA QUE LAS OTRAS PANTALLAS

   App ya reserva 4rem para el navbar.
   La página mantiene altura natural. Sólo el cuerpo del
   modal puede desplazarse; su cabecera permanece visible.
   Todos los estilos están aislados bajo solcont-services.
====================================================== */

const RESPONSIVE_STYLES = `
  .solcont-services {
    --sv-header-offset: 4rem;
    --sv-section-space: clamp(2.5rem, 6vh, 5rem);
    --sv-hero-space: clamp(1.75rem, 5vh, 4rem);
    --sv-stack: clamp(1rem, 2.5vh, 1.75rem);
    --sv-gap: clamp(1.5rem, 3vw, 3rem);
    --sv-panel: clamp(1.125rem, 2vw, 1.625rem);
  }
  .solcont-services .sv-container {
    box-sizing: border-box;
    width: 100%;
    max-width: 75rem;
    margin-inline: auto;
    padding-inline: clamp(1rem, 3vw, 2.5rem);
  }
  .solcont-services .sv-hero { padding-block: var(--sv-hero-space); }
  .solcont-services .sv-accent { margin-top: 1rem; }
  .solcont-services .sv-spaced { margin-top: var(--sv-stack); }
  .solcont-services .sv-hero-title {
    margin-top: var(--sv-stack);
    font-size: clamp(2rem, 6vw, 3.5rem);
    line-height: 1.04;
    text-wrap: balance;
    overflow-wrap: anywhere;
  }
  .solcont-services .sv-copy { font-size: 1rem; line-height: 1.6; }
  .solcont-services .sv-navigation { margin-top: var(--sv-gap); }
  .solcont-services .sv-nav-list { gap: 0.5rem; }
  .solcont-services .sv-nav-list > li { min-width: 0; max-width: 100%; }
  .solcont-services .sv-nav-link { max-width: 100%; white-space: normal; overflow-wrap: anywhere; }
  .solcont-services .sv-categories { padding-bottom: var(--sv-section-space); }
  .solcont-services .sv-category {
    padding-block: var(--sv-section-space);
    scroll-margin-top: calc(var(--sv-header-offset) + 1rem);
  }
  .solcont-services .sv-category-header {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--sv-gap);
  }
  .solcont-services .sv-category-number { width: 2.25rem; height: 2.25rem; }
  .solcont-services .sv-title {
    margin-top: var(--sv-stack);
    font-size: clamp(1.75rem, 3.2vw, 2.5rem);
    line-height: 1.1;
    text-wrap: balance;
    overflow-wrap: anywhere;
  }
  .solcont-services .sv-plans { margin-top: var(--sv-gap); }
  .solcont-services .sv-panel { padding: var(--sv-panel); }
  .solcont-services .sv-price { font-size: clamp(2rem, 4vw, 2.75rem); line-height: 1.1; }
  .solcont-services .sv-small-copy { font-size: 0.875rem; line-height: 1.6; }
  .solcont-services .sv-service-grid {
    margin-top: var(--sv-gap);
    gap: 1rem;
    grid-template-columns: minmax(0, 1fr);
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 17rem), 1fr));
  }
  .solcont-services .sv-card-rule { margin-bottom: 1rem; }
  .solcont-services .sv-card-title { font-size: 1.125rem; line-height: 1.3; overflow-wrap: anywhere; }
  .solcont-services .sv-benefits { margin-top: var(--sv-stack); }
  .solcont-services .sv-benefits-list { margin-top: 1rem; gap: 1rem; }
  .solcont-services .sv-note { margin-top: 1rem; line-height: 1.6; }
  .solcont-services .sv-button {
    min-height: 2.75rem;
    max-width: 100%;
    padding: 0.75rem 1.25rem;
    line-height: 1.4;
    text-align: center;
    white-space: normal;
    overflow-wrap: anywhere;
  }
  .solcont-services .sv-button > svg { flex-shrink: 0; }

  /* Un único área de scroll en el modal, con altura del viewport visible.
     Los valores en px se actualizan al abrir/cerrar el teclado, sin
     cambiar el estado del formulario ni enfocar campos de texto. */
  .solcont-services .sv-modal {
    --sv-viewport-height: 100vh;
    --sv-viewport-top: 0px;
    --sv-dialog-gap: max(1rem, env(safe-area-inset-top), env(safe-area-inset-bottom), env(safe-area-inset-left), env(safe-area-inset-right));
    --sv-modal-space: clamp(1rem, 2.5vw, 1.75rem);
    --sv-form-gap: 1.125rem;
    --sv-dialog-height: calc(var(--sv-viewport-height) - var(--sv-dialog-gap) - var(--sv-dialog-gap));
    position: fixed;
    top: calc(var(--sv-viewport-top) + var(--sv-viewport-height) / 2);
    bottom: auto;
    left: 0;
    right: 0;
    margin-block: 0;
    margin-inline: auto;
    transform: translateY(-50%);
    box-sizing: border-box;
    width: calc(100% - var(--sv-dialog-gap) - var(--sv-dialog-gap));
    max-width: 40rem;
    max-height: var(--sv-dialog-height);
  }
  .solcont-services .sv-modal-shell { max-height: var(--sv-dialog-height); }
  .solcont-services .sv-modal-header { padding: 0.875rem var(--sv-modal-space); gap: 0.75rem; }
  .solcont-services .sv-modal-title { font-size: 1rem; line-height: 1.4; overflow-wrap: anywhere; }
  .solcont-services .sv-modal-body {
    flex: 1 1 auto;
    padding: var(--sv-modal-space);
    scroll-padding-block: 1rem;
    -webkit-overflow-scrolling: touch;
  }
  .solcont-services .sv-form { min-width: 0; }
  .solcont-services .sv-progress { margin-bottom: var(--sv-form-gap); }
  .solcont-services .sv-question { font-size: clamp(1.125rem, 2.2vw, 1.375rem); line-height: 1.25; overflow-wrap: anywhere; }
  .solcont-services .sv-fields { margin-top: var(--sv-form-gap); }
  .solcont-services .sv-name-grid {
    display: grid;
    gap: 1rem;
    grid-template-columns: minmax(0, 1fr);
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 13rem), 1fr));
  }
  .solcont-services .sv-field-stack > * + * { margin-top: var(--sv-form-gap); }
  .solcont-services .sv-field {
    min-height: 2.75rem;
    padding: 0.625rem 0.875rem;
    font-size: 1rem;
    line-height: 1.5;
    scroll-margin-block: 1rem;
  }
  .solcont-services .sv-choice-grid {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: minmax(0, 1fr);
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 12rem), 1fr));
  }
  .solcont-services .sv-choice-card { min-height: 3rem; padding: 0.75rem; gap: 0.75rem; }
  .solcont-services .sv-choice-text { overflow-wrap: anywhere; }
  .solcont-services .sv-review-row { padding: 0.75rem 1rem; }
  .solcont-services .sv-review-value { overflow-wrap: anywhere; }
  .solcont-services .sv-form-actions { margin-top: var(--sv-form-gap); padding-top: 1rem; }
  .solcont-services .sv-success { padding-block: 1.5rem; }

  @supports (height: 100svh) {
    .solcont-services {
      --sv-section-space: clamp(2.5rem, 6svh, 5rem);
      --sv-hero-space: clamp(1.75rem, 5svh, 4rem);
      --sv-stack: clamp(1rem, 2.5svh, 1.75rem);
    }
  }
  @supports (height: 100dvh) {
    .solcont-services .sv-modal { --sv-viewport-height: 100dvh; }
  }

  @media (min-width: 60rem) {
    .solcont-services .sv-category-header {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      align-items: end;
    }
    .solcont-services .sv-hero-title {
      font-size: clamp(2.375rem, min(4.2vw, 7.2vh), 4.25rem);
    }
    @supports (height: 100svh) {
      .solcont-services .sv-hero-title {
        font-size: clamp(2.375rem, min(4.2vw, 7.2svh), 4.25rem);
      }
    }
  }
  @media (min-width: 60rem) and (max-height: 46.25rem) {
    .solcont-services {
      --sv-section-space: 2rem;
      --sv-hero-space: 1.5rem;
      --sv-stack: 1rem;
      --sv-gap: 1.5rem;
      --sv-panel: 1.125rem;
    }
    .solcont-services .sv-hero-title {
      font-size: clamp(2.25rem, min(4.2vw, 6.8vh), 3.25rem);
    }
  }
  @media (max-height: 46.25rem) {
    .solcont-services .sv-modal { --sv-modal-space: 1rem; --sv-form-gap: 1rem; }
    .solcont-services .sv-modal-header { padding-block: 0.625rem; }
    .solcont-services .sv-success { padding-block: 1rem; }
  }
  @media (max-width: 39.999rem) {
    .solcont-services .sv-button { width: 100%; }
    .solcont-services .sv-modal {
      --sv-dialog-gap: max(0.75rem, env(safe-area-inset-top), env(safe-area-inset-bottom), env(safe-area-inset-left), env(safe-area-inset-right));
      border-radius: 1.25rem;
    }
    .solcont-services .sv-form-actions { flex-direction: column; align-items: stretch; }
    .solcont-services .sv-form-actions > button { width: 100%; justify-content: center; }
    .solcont-services .sv-form-actions > span:empty { display: none; }
  }
`;

const PRIMARY_BUTTON = `
  sv-button inline-flex items-center justify-center gap-3
  rounded-xl bg-[#1648EE] px-6 py-3 text-sm font-bold text-white
  transition-colors hover:bg-[#103AC7]
  focus-visible:outline focus-visible:outline-2
  focus-visible:outline-offset-4 focus-visible:outline-[#1648EE]
  disabled:cursor-not-allowed disabled:opacity-50
`;

const FIELD_CLASS = `
  sv-field w-full min-w-0 rounded-xl border bg-white
  text-base text-[#081B3E] placeholder:text-slate-400
  outline-none transition-colors
  focus:border-[#1648EE] focus:ring-2 focus:ring-[#1648EE]/15
  disabled:opacity-60
`;

/* ======================================================
   IDIOMA
   Compatible con MainScreen y AboutScreen.
====================================================== */

const LANGUAGE_STORAGE_KEY = "solcont-language";
const LANGUAGE_CHANGE_EVENT = "solcont:language-change";

function normalizeLanguage(value) {
  if (typeof value !== "string") return null;

  const code = value.trim().toLowerCase().split(/[-_]/)[0];

  return ["es", "en", "fr"].includes(code) ? code : null;
}

function readStoredLanguage() {
  if (typeof window === "undefined") return "es";

  try {
    return (
      normalizeLanguage(
        window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
      ) || "es"
    );
  } catch {
    return "es";
  }
}

function useLanguage(controlledLanguage) {
  const [storedLanguage, setStoredLanguage] = useState(readStoredLanguage);

  useEffect(() => {
    function handleLanguageChange(event) {
      const value =
        typeof event.detail === "string"
          ? event.detail
          : event.detail?.language;

      const next = normalizeLanguage(value);
      if (!next) return;

      setStoredLanguage(next);

      try {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
      } catch {
        // El idioma sigue funcionando si el almacenamiento está bloqueado.
      }
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY || event.key === null) {
        setStoredLanguage(readStoredLanguage());
      }
    }

    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        LANGUAGE_CHANGE_EVENT,
        handleLanguageChange
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return normalizeLanguage(controlledLanguage) || storedLanguage;
}

/* ======================================================
   SERVICIOS
   IDs estables: se conservan para navegación y CRM.
====================================================== */

const CATEGORIES = {
  es: [
    {
      id: "contabilidad",
      nav: "Contabilidad",
      eyebrow: "CONTABILIDAD + CUMPLIMIENTO",
      title: "Tus números en orden. Tu negocio con dirección.",
      desc:
        "Operación contable para personas físicas y morales en México. Procesos claros, documentación organizada e información útil para decidir.",
      items: [
        [
          "Contabilidad electrónica",
          "Pólizas, balanza, papeles de trabajo y conciliaciones bancarias con integración de CFDI.",
        ],
        [
          "Declaraciones fiscales",
          "Declaraciones mensuales, anuales, DIOT y demás obligaciones aplicables a tu régimen.",
        ],
        [
          "Facturación CFDI",
          "Emisión, timbrado, cancelaciones y complementos conforme a las necesidades de tu operación.",
        ],
        [
          "Nómina e IMSS / INFONAVIT",
          "Altas, bajas, timbrado de nómina, SUA, SIPARE y seguimiento de obligaciones patronales.",
        ],
        [
          "Tesorería y flujo de caja",
          "Cuentas por cobrar y pagar, programación de pagos y proyección de flujo.",
        ],
        [
          "Reportes financieros",
          "Estados financieros e indicadores para entender resultados y tomar decisiones.",
        ],
        [
          "Atención al SAT",
          "Acompañamiento en cartas invitación, requerimientos, devoluciones y aclaraciones.",
        ],
        [
          "Inicio y cambio de contador",
          "Revisión de documentación, saldos y pendientes para una transición organizada.",
        ],
      ],
      cta: "Quiero asesoría contable",
    },
    {
      id: "global",
      nav: "Conexión global",
      eyebrow: "CONEXIÓN GLOBAL",
      title: "Tu actividad cruza fronteras. La claridad también.",
      desc:
        "Atención contable, fiscal y lingüística para extranjeros en México, empresas con operaciones internacionales y profesionales que trabajan en varios idiomas.",
      items: [
        [
          "Traducción de documentos",
          "Facturas, contratos, reportes y documentación fiscal en español, inglés y francés.",
        ],
        [
          "Interpretación consecutiva",
          "Acompañamiento lingüístico en reuniones presenciales o por videollamada.",
        ],
        [
          "Alta fiscal de extranjeros",
          "Orientación y acompañamiento para RFC, e.firma y elección de estructura fiscal en México.",
        ],
        [
          "Fiscalidad internacional",
          "Revisión de residencia fiscal, ingresos y posible aplicación de convenios para evitar la doble tributación.",
        ],
      ],
      note:
        "El alcance se determina según tu caso. La aplicación de convenios requiere revisar la residencia fiscal, el tipo de ingreso y los requisitos correspondientes. Las traducciones certificadas y la asesoría migratoria se cotizan o coordinan por separado cuando sean necesarias.",
      cta: "Quiero orientación internacional",
    },
    {
      id: "tigo",
      nav: "TiGo",
      eyebrow: "SOLUCIONES DIGITALES / TIGO",
      title: "Menos desorden. Más control de tu tienda.",
      desc:
        "Ventas, inventarios y caja conectados en un sistema en la nube para tiendas y boutiques.",
      plans: [
        {
          id: "tienda",
          name: "Tiendas",
          price: "$399",
          desc: "Ventas, inventario, caja y reportes.",
        },
        {
          id: "boutique",
          name: "Boutiques",
          price: "$499",
          desc: "Además: apartados, cambios, devoluciones y tallas.",
        },
      ],
      items: [
        ["Control de ventas", "Registro de ventas, historial de tickets y seguimiento de operaciones."],
        ["Inventarios", "Entradas, salidas, ajustes y alertas de existencias."],
        ["Caja y cortes", "Apertura, cierre y conciliación de efectivo y otros medios de pago."],
        ["Reportes", "Ventas por periodo, productos destacados, márgenes y tendencias."],
        ["Apartados y cambios", "Seguimiento de anticipos, cambios de mercancía y devoluciones."],
        ["Multi-sucursal", "Supervisión de varias tiendas desde un mismo entorno, según el plan contratado."],
      ],
      note:
        "Precios mensuales en MXN. Confirma las funciones, sucursales y condiciones incluidas antes de contratar.",
      cta: "Quiero conocer TiGo",
    },
    {
      id: "software",
      nav: "Software",
      eyebrow: "DESARROLLO A LA MEDIDA",
      title: "Tu proceso no es genérico. Tu software tampoco.",
      desc:
        "Diseñamos soluciones para conectar información, reducir tareas repetitivas y dar visibilidad a tu operación.",
      items: [
        ["Dashboards y KPIs", "Indicadores de negocio organizados en paneles claros."],
        ["Análisis de negocio", "Modelos de datos, proyecciones y reportes para apoyar tus decisiones."],
        ["ERP", "Integración de finanzas, compras, inventario y operaciones."],
        ["CRM", "Clientes, oportunidades, seguimiento y automatización comercial."],
        ["WMS", "Recepción, ubicaciones, preparación de pedidos, empaque y despacho."],
        ["Integraciones y APIs", "Conexión entre tus sistemas y plataformas de terceros."],
      ],
      benefits: [
        "Diseño a partir de tu proceso real.",
        "Alcance y costos definidos por proyecto.",
        "Soporte y evolución según el acuerdo contratado.",
        "Entregas por fases con objetivos verificables.",
      ],
      cta: "Cuéntanos tu proyecto",
    },
  ],

  en: [
    {
      id: "contabilidad",
      nav: "Accounting",
      eyebrow: "ACCOUNTING + COMPLIANCE",
      title: "Organized numbers. Clear business direction.",
      desc:
        "Accounting support for individuals and companies in Mexico. Clear processes, organized records and useful information for your decisions.",
      items: [
        ["Electronic accounting", "Journal entries, trial balances, working papers and bank reconciliations with CFDI integration."],
        ["Tax returns", "Monthly and annual returns, DIOT and other obligations applicable to your tax regime."],
        ["CFDI invoicing", "Issuance, fiscal validation, cancellations and complementary documents for your operations."],
        ["Payroll and IMSS / INFONAVIT", "Employee registrations, deregistrations, payroll receipts, SUA, SIPARE and employer compliance support."],
        ["Treasury and cash flow", "Receivables, payables, payment scheduling and cash flow projections."],
        ["Financial reports", "Financial statements and indicators to understand performance and support decisions."],
        ["SAT matters", "Support with SAT notices, information requests, refunds and clarifications."],
        ["Setup and accountant transition", "Review of documents, balances and outstanding matters for an organized transition."],
      ],
      cta: "I need accounting advice",
    },
    {
      id: "global",
      nav: "Global support",
      eyebrow: "GLOBAL CONNECTION",
      title: "Your work crosses borders. Clarity should too.",
      desc:
        "Accounting, tax and language support for foreign nationals in Mexico, internationally active companies and multilingual professionals.",
      items: [
        ["Document translation", "Invoices, contracts, reports and tax documentation in Spanish, English and French."],
        ["Consecutive interpreting", "Language support for in-person meetings and video calls."],
        ["Tax registration for foreign nationals", "Guidance and support with RFC, e.firma and tax structure selection in Mexico."],
        ["International tax matters", "A review of tax residency, income and potential eligibility under double-taxation treaties."],
      ],
      note:
        "The scope depends on your case. Treaty eligibility requires reviewing tax residency, income type and applicable conditions. Certified translations and immigration advice are quoted or coordinated separately when needed.",
      cta: "I need international guidance",
    },
    {
      id: "tigo",
      nav: "TiGo",
      eyebrow: "DIGITAL SOLUTIONS / TIGO",
      title: "Less disorder. More control of your store.",
      desc:
        "Sales, inventory and cash management connected through a cloud-based system for stores and boutiques.",
      plans: [
        {
          id: "tienda",
          name: "Stores",
          price: "$399",
          desc: "Sales, inventory, cash management and reports.",
        },
        {
          id: "boutique",
          name: "Boutiques",
          price: "$499",
          desc: "Also includes layaways, exchanges, returns and sizes.",
        },
      ],
      items: [
        ["Sales management", "Sales recording, receipt history and transaction tracking."],
        ["Inventory", "Stock movements, adjustments and inventory alerts."],
        ["Cash management", "Opening, closing and reconciliation of cash and other payment methods."],
        ["Reports", "Sales by period, leading products, margins and trends."],
        ["Layaways and exchanges", "Tracking of deposits, merchandise exchanges and returns."],
        ["Multiple locations", "Oversee several stores from one environment, depending on your plan."],
      ],
      note:
        "Monthly prices in MXN. Confirm included features, locations and terms before subscribing.",
      cta: "Tell me about TiGo",
    },
    {
      id: "software",
      nav: "Software",
      eyebrow: "CUSTOM DEVELOPMENT",
      title: "Your process is unique. Your software should be too.",
      desc:
        "Solutions designed to connect information, reduce repetitive tasks and improve operational visibility.",
      items: [
        ["Dashboards and KPIs", "Business indicators organized into clear dashboards."],
        ["Business analytics", "Data models, projections and reports to support decisions."],
        ["ERP", "Integration of finance, purchasing, inventory and operations."],
        ["CRM", "Customers, opportunities, follow-ups and sales automation."],
        ["WMS", "Receiving, storage locations, order picking, packing and dispatch."],
        ["Integrations and APIs", "Connections between your systems and third-party platforms."],
      ],
      benefits: [
        "Designed around your actual workflow.",
        "Project-specific scope and costs.",
        "Support and improvements under the agreed terms.",
        "Phased delivery with verifiable objectives.",
      ],
      cta: "Tell us about your project",
    },
  ],

  fr: [
    {
      id: "contabilidad",
      nav: "Comptabilité",
      eyebrow: "COMPTABILITÉ + CONFORMITÉ",
      title: "Des comptes organisés. Une direction claire.",
      desc:
        "Un accompagnement comptable pour les particuliers et les entreprises au Mexique. Des processus clairs, des documents organisés et des informations utiles pour décider.",
      items: [
        ["Comptabilité électronique", "Écritures, balance, documents de travail et rapprochements bancaires avec intégration des CFDI."],
        ["Déclarations fiscales", "Déclarations mensuelles, annuelles, DIOT et autres obligations propres à votre régime."],
        ["Facturation CFDI", "Émission, validation fiscale, annulations et compléments adaptés à votre activité."],
        ["Paie et IMSS / INFONAVIT", "Inscriptions et radiations des salariés, justificatifs de paie, SUA, SIPARE et suivi des obligations patronales."],
        ["Trésorerie", "Créances, dettes fournisseurs, planification des paiements et prévisions de trésorerie."],
        ["Rapports financiers", "États financiers et indicateurs pour comprendre les résultats et éclairer vos décisions."],
        ["Démarches auprès du SAT", "Accompagnement pour les courriers, demandes d’information, remboursements et clarifications."],
        ["Démarrage et changement de comptable", "Examen des documents, des soldes et des dossiers en cours pour une transition organisée."],
      ],
      cta: "Je souhaite un conseil comptable",
    },
    {
      id: "global",
      nav: "International",
      eyebrow: "CONNEXION INTERNATIONALE",
      title: "Votre activité dépasse les frontières. La clarté aussi.",
      desc:
        "Un accompagnement comptable, fiscal et linguistique pour les étrangers au Mexique, les entreprises internationales et les professionnels multilingues.",
      items: [
        ["Traduction de documents", "Factures, contrats, rapports et documents fiscaux en espagnol, en anglais et en français."],
        ["Interprétation consécutive", "Accompagnement linguistique lors de réunions en personne ou en visioconférence."],
        ["Inscription fiscale des étrangers", "Conseils et accompagnement pour le RFC, l’e.firma et le choix de la structure fiscale au Mexique."],
        ["Fiscalité internationale", "Examen de la résidence fiscale, des revenus et de l’application éventuelle des conventions contre la double imposition."],
      ],
      note:
        "Le périmètre dépend de votre dossier. L’application d’une convention exige l’examen de la résidence fiscale, de la nature des revenus et des conditions requises. Les traductions certifiées et les conseils en immigration font l’objet d’une coordination ou d’un devis distinct si nécessaire.",
      cta: "Je souhaite un conseil international",
    },
    {
      id: "tigo",
      nav: "TiGo",
      eyebrow: "SOLUTIONS NUMÉRIQUES / TIGO",
      title: "Moins de désordre. Plus de contrôle.",
      desc:
        "Ventes, stocks et caisse réunis dans un système infonuagique pour les commerces et les boutiques.",
      plans: [
        {
          id: "tienda",
          name: "Commerces",
          price: "$399",
          desc: "Ventes, stocks, caisse et rapports.",
        },
        {
          id: "boutique",
          name: "Boutiques",
          price: "$499",
          desc: "Avec mises de côté, échanges, retours et tailles.",
        },
      ],
      items: [
        ["Gestion des ventes", "Enregistrement des ventes, historique des tickets et suivi des opérations."],
        ["Stocks", "Entrées, sorties, ajustements et alertes de stock."],
        ["Gestion de caisse", "Ouverture, clôture et rapprochement des espèces et autres moyens de paiement."],
        ["Rapports", "Ventes par période, produits phares, marges et tendances."],
        ["Mises de côté et échanges", "Suivi des acomptes, des échanges et des retours de marchandises."],
        ["Plusieurs établissements", "Supervision de plusieurs magasins dans un même environnement, selon le forfait."],
      ],
      note:
        "Tarifs mensuels en MXN. Confirmez les fonctionnalités, les établissements et les conditions incluses avant de souscrire.",
      cta: "Je souhaite découvrir TiGo",
    },
    {
      id: "software",
      nav: "Logiciels",
      eyebrow: "DÉVELOPPEMENT SUR MESURE",
      title: "Vos processus sont uniques. Votre logiciel aussi.",
      desc:
        "Des solutions pour relier les informations, réduire les tâches répétitives et améliorer la visibilité de vos opérations.",
      items: [
        ["Tableaux de bord et KPI", "Des indicateurs métier organisés dans des tableaux de bord clairs."],
        ["Analyse d’activité", "Modèles de données, prévisions et rapports pour éclairer vos décisions."],
        ["ERP", "Intégration des finances, des achats, des stocks et des opérations."],
        ["CRM", "Clients, opportunités, suivi et automatisation commerciale."],
        ["WMS", "Réception, emplacements, préparation des commandes, emballage et expédition."],
        ["Intégrations et API", "Connexion de vos systèmes aux plateformes tierces."],
      ],
      benefits: [
        "Une conception fondée sur vos processus réels.",
        "Un périmètre et des coûts définis par projet.",
        "Un accompagnement selon les conditions convenues.",
        "Des livraisons par étapes aux objectifs vérifiables.",
      ],
      cta: "Parlons de votre projet",
    },
  ],
};

/* ======================================================
   INTERFAZ Y FORMULARIOS TRILINGÜES
====================================================== */

const UI = {
  es: {
    eyebrow: "SOLCONT / SERVICIOS",
    title: "Claridad para decidir.",
    accent: "Soluciones para avanzar.",
    intro:
      "Contabilidad, estrategia fiscal, atención internacional y tecnología. Conecta lo que tu negocio necesita en una misma conversación.",
    navigation: "Explorar servicios",
    benefits: "Cómo trabajamos",
    monthly: "MXN / mes",
    close: "Cerrar",
    back: "Anterior",
    next: "Continuar",
    send: "Enviar solicitud",
    sending: "Enviando…",
    step: "Paso",
    of: "de",
    steps: ["Nombre", "Tu situación", "Contacto", "Confirmación"],
    questions: {
      name: "Empecemos por conocerte.",
      contabilidad: "Cuéntanos sobre tu situación fiscal.",
      global: "¿Desde dónde trabajas?",
      tigo: "Conozcamos tu negocio.",
      software: "¿Qué necesitas desarrollar?",
      contact: "¿Cómo podemos contactarte?",
      review: "Revisa tu información.",
    },
    labels: {
      nombre: "Nombre(s)",
      apellidos: "Apellidos",
      regimen: "Régimen fiscal",
      pais: "País de origen / residencia",
      idioma: "Idioma de preferencia",
      tipoNegocio: "Tipo de negocio",
      sucursales: "Número de sucursales",
      tipoSoftware: "Tipo de proyecto",
      descripcion: "Comentarios",
      telefono: "Teléfono",
      whatsapp: "¿Este número tiene WhatsApp?",
      correo: "Correo electrónico",
    },
    optional: "opcional",
    descriptionLabels: {
      contabilidad: "¿Cuál es tu situación fiscal actual?",
      global: "¿En qué necesitas apoyo?",
      software: "Describe tu proyecto",
    },
    descriptionPlaceholders: {
      contabilidad: "Actividad, régimen, obligaciones o pendientes que deseas revisar…",
      global: "Actividad, país de residencia y dudas sobre tu situación en México…",
      software: "Qué proceso deseas mejorar, qué utilizas hoy y qué resultado necesitas…",
    },
    phonePlaceholder: "+52 238 123 4567",
    emailPlaceholder: "nombre@empresa.com",
    options: {
      regimen: [
        ["pfae", "PFAE", "Persona Física con Actividad Empresarial"],
        ["resico", "RESICO", "Régimen Simplificado de Confianza"],
        ["pm", "Persona Moral", "Empresa o sociedad"],
        ["no_se", "No estoy seguro", "Necesito orientación"],
      ],
      tipoNegocio: [
        ["tienda", "Tienda", "$399 MXN / mes"],
        ["boutique", "Boutique", "$499 MXN / mes"],
      ],
      tipoSoftware: [
        ["kpi", "Dashboard / KPIs"],
        ["erp", "ERP"],
        ["crm", "CRM"],
        ["wms", "WMS"],
        ["integracion", "Integración / API"],
        ["otro", "Otro"],
      ],
      whatsapp: [
        [true, "Sí, tiene WhatsApp"],
        [false, "No, solo llamadas"],
      ],
    },
    errors: {
      required: "Completa este campo.",
      choice: "Selecciona una opción.",
      phone: "Ingresa un teléfono válido de 10 a 15 dígitos.",
      email: "Ingresa un correo electrónico válido.",
      save: "No pudimos enviar la solicitud. Tus datos siguen aquí; inténtalo nuevamente.",
    },
    reviewNote: "Revisaremos tu solicitud para dar seguimiento por los medios que compartiste.",
    successTitle: "Solicitud recibida",
    successBody: "Gracias, {name}. Tenemos tu información y daremos seguimiento a tu solicitud.",
  },

  en: {
    eyebrow: "SOLCONT / SERVICES",
    title: "Clarity for decisions.",
    accent: "Solutions for progress.",
    intro:
      "Accounting, tax strategy, international support and technology. Connect your business needs in one conversation.",
    navigation: "Explore services",
    benefits: "How we work",
    monthly: "MXN / month",
    close: "Close",
    back: "Back",
    next: "Continue",
    send: "Send request",
    sending: "Sending…",
    step: "Step",
    of: "of",
    steps: ["Name", "Your situation", "Contact", "Review"],
    questions: {
      name: "Let’s get to know you.",
      contabilidad: "Tell us about your tax situation.",
      global: "Where do you work from?",
      tigo: "Tell us about your business.",
      software: "What would you like to build?",
      contact: "How can we contact you?",
      review: "Review your information.",
    },
    labels: {
      nombre: "First name(s)",
      apellidos: "Last name(s)",
      regimen: "Tax regime",
      pais: "Country of origin / residence",
      idioma: "Preferred language",
      tipoNegocio: "Business type",
      sucursales: "Number of locations",
      tipoSoftware: "Project type",
      descripcion: "Comments",
      telefono: "Phone",
      whatsapp: "Does this number have WhatsApp?",
      correo: "Email",
    },
    optional: "optional",
    descriptionLabels: {
      contabilidad: "What is your current tax situation?",
      global: "What do you need help with?",
      software: "Describe your project",
    },
    descriptionPlaceholders: {
      contabilidad: "Your activity, tax regime or outstanding matters you would like to review…",
      global: "Your activity, country of residence and questions about your situation in Mexico…",
      software: "The process you want to improve, your current tools and your desired outcome…",
    },
    phonePlaceholder: "+1 514 555 0123",
    emailPlaceholder: "name@company.com",
    options: {
      regimen: [
        ["pfae", "PFAE", "Individual with business activity in Mexico"],
        ["resico", "RESICO", "Mexico’s simplified trust tax regime"],
        ["pm", "Legal entity", "Company or corporation"],
        ["no_se", "Not sure", "I need guidance"],
      ],
      tipoNegocio: [
        ["tienda", "Store", "$399 MXN / month"],
        ["boutique", "Boutique", "$499 MXN / month"],
      ],
      tipoSoftware: [
        ["kpi", "Dashboard / KPIs"],
        ["erp", "ERP"],
        ["crm", "CRM"],
        ["wms", "WMS"],
        ["integracion", "Integration / API"],
        ["otro", "Other"],
      ],
      whatsapp: [
        [true, "Yes, it has WhatsApp"],
        [false, "No, calls only"],
      ],
    },
    errors: {
      required: "Complete this field.",
      choice: "Select an option.",
      phone: "Enter a valid phone number with 10 to 15 digits.",
      email: "Enter a valid email address.",
      save: "We could not send your request. Your information is still here; please try again.",
    },
    reviewNote: "We will review your request and follow up using the contact details you provided.",
    successTitle: "Request received",
    successBody: "Thank you, {name}. We have received your information and will follow up on your request.",
  },

  fr: {
    eyebrow: "SOLCONT / SERVICES",
    title: "La clarté pour décider.",
    accent: "Des solutions pour avancer.",
    intro:
      "Comptabilité, stratégie fiscale, accompagnement international et technologie. Réunissons les besoins de votre entreprise dans une même conversation.",
    navigation: "Explorer les services",
    benefits: "Notre méthode",
    monthly: "MXN / mois",
    close: "Fermer",
    back: "Retour",
    next: "Continuer",
    send: "Envoyer la demande",
    sending: "Envoi en cours…",
    step: "Étape",
    of: "sur",
    steps: ["Nom", "Votre situation", "Coordonnées", "Vérification"],
    questions: {
      name: "Faisons connaissance.",
      contabilidad: "Parlons de votre situation fiscale.",
      global: "Depuis quel pays travaillez-vous ?",
      tigo: "Parlez-nous de votre commerce.",
      software: "Que souhaitez-vous développer ?",
      contact: "Comment vous contacter ?",
      review: "Vérifiez vos informations.",
    },
    labels: {
      nombre: "Prénom(s)",
      apellidos: "Nom(s)",
      regimen: "Régime fiscal",
      pais: "Pays d’origine / de résidence",
      idioma: "Langue de préférence",
      tipoNegocio: "Type de commerce",
      sucursales: "Nombre d’établissements",
      tipoSoftware: "Type de projet",
      descripcion: "Commentaires",
      telefono: "Téléphone",
      whatsapp: "Ce numéro dispose-t-il de WhatsApp ?",
      correo: "Courriel",
    },
    optional: "facultatif",
    descriptionLabels: {
      contabilidad: "Quelle est votre situation fiscale actuelle ?",
      global: "De quel accompagnement avez-vous besoin ?",
      software: "Décrivez votre projet",
    },
    descriptionPlaceholders: {
      contabilidad: "Votre activité, votre régime ou les dossiers que vous souhaitez examiner…",
      global: "Votre activité, votre pays de résidence et vos questions concernant le Mexique…",
      software: "Le processus à améliorer, vos outils actuels et le résultat souhaité…",
    },
    phonePlaceholder: "+33 6 12 34 56 78",
    emailPlaceholder: "nom@entreprise.com",
    options: {
      regimen: [
        ["pfae", "PFAE", "Personne physique exerçant une activité entrepreneuriale"],
        ["resico", "RESICO", "Régime fiscal simplifié de confiance au Mexique"],
        ["pm", "Personne morale", "Entreprise ou société"],
        ["no_se", "Je ne sais pas", "J’ai besoin de conseils"],
      ],
      tipoNegocio: [
        ["tienda", "Commerce", "$399 MXN / mois"],
        ["boutique", "Boutique", "$499 MXN / mois"],
      ],
      tipoSoftware: [
        ["kpi", "Tableau de bord / KPI"],
        ["erp", "ERP"],
        ["crm", "CRM"],
        ["wms", "WMS"],
        ["integracion", "Intégration / API"],
        ["otro", "Autre"],
      ],
      whatsapp: [
        [true, "Oui, avec WhatsApp"],
        [false, "Non, appels uniquement"],
      ],
    },
    errors: {
      required: "Remplissez ce champ.",
      choice: "Sélectionnez une option.",
      phone: "Saisissez un numéro valide de 10 à 15 chiffres.",
      email: "Saisissez une adresse courriel valide.",
      save: "L’envoi a échoué. Vos informations sont conservées dans ce formulaire ; veuillez réessayer.",
    },
    reviewNote: "Nous examinerons votre demande et vous contacterons aux coordonnées indiquées.",
    successTitle: "Demande reçue",
    successBody: "Merci, {name}. Nous avons reçu vos informations et donnerons suite à votre demande.",
  },
};

/* ======================================================
   SCHÉMAS: VALORES ORIGINALES DEL CRM
====================================================== */

const CONTEXT_FIELDS = {
  contabilidad: ["regimen", "descripcion"],
  global: ["pais", "idioma", "descripcion"],
  tigo: ["tipoNegocio", "sucursales"],
  software: ["tipoSoftware", "descripcion"],
};

const ALLOWED_VALUES = {
  regimen: ["pfae", "resico", "pm", "no_se"],
  idioma: ["es", "en", "fr"],
  tipoNegocio: ["tienda", "boutique"],
  sucursales: ["1", "2", "3", "4+"],
  tipoSoftware: ["kpi", "erp", "crm", "wms", "integracion", "otro"],
  whatsapp: [true, false],
};

const LANGUAGE_OPTIONS = [
  ["es", "Español"],
  ["en", "English"],
  ["fr", "Français"],
];

function createEmptyForm() {
  return {
    nombre: "",
    apellidos: "",
    regimen: "",
    pais: "",
    idioma: "",
    tipoNegocio: "",
    sucursales: "",
    tipoSoftware: "",
    descripcion: "",
    telefono: "",
    whatsapp: null,
    correo: "",
  };
}

function fieldsForStep(formType, step) {
  if (step === 0) return ["nombre", "apellidos"];
  if (step === 1) return CONTEXT_FIELDS[formType];
  if (step === 2) return ["telefono", "whatsapp", "correo"];
  return [];
}

function validateStep(form, formType, step) {
  const errors = {};

  for (const field of fieldsForStep(formType, step)) {
    const value = form[field];

    if (field === "sucursales" && value === "") continue;

    if (ALLOWED_VALUES[field]) {
      if (!ALLOWED_VALUES[field].includes(value)) {
        errors[field] = "choice";
      }
      continue;
    }

    if (field === "telefono") {
      const phone = value.trim();
      const digits = phone.replace(/\D/g, "");

      if (
        !/^\+?[\d\s().-]+$/.test(phone) ||
        digits.length < 10 ||
        digits.length > 15
      ) {
        errors[field] = "phone";
      }

      continue;
    }

    if (field === "correo") {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim())) {
        errors[field] = "email";
      }

      continue;
    }

    if (!value.trim()) errors[field] = "required";
  }

  return errors;
}

function getUtmParams() {
  try {
    const params = new URLSearchParams(window.location.search);

    return {
      source: params.get("utm_source") || null,
      medium: params.get("utm_medium") || null,
      campaign: params.get("utm_campaign") || null,
      term: params.get("utm_term") || null,
      content: params.get("utm_content") || null,
    };
  } catch {
    return {};
  }
}

/* ======================================================
   PANTALLA
====================================================== */

export default function ServicesScreen({
  language: controlledLanguage,
} = {}) {
  const language = useLanguage(controlledLanguage);
  const t = UI[language];
  const categories = CATEGORIES[language];

  // Guardamos solamente el ID, no un título ya traducido.
  const [modalType, setModalType] = useState(null);
  const selectedCategory = categories.find((cat) => cat.id === modalType);

  return (
    <div lang={language} className="solcont-services bg-[#F7F3E9] text-[#081B3E]">
      <style>{RESPONSIVE_STYLES}</style>
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-[#1648EE] text-[#F7F3E9]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={GRID_BACKGROUND}
        />

        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-64 -top-40 h-[640px] w-[640px]"
        >
          <div className="absolute inset-0 rounded-full border border-white/20" />
          <div className="absolute inset-24 rounded-full border border-white/20" />
          <span
            className="absolute left-[84px] top-[90px] h-5 w-5 rounded-full bg-[#F72585]"
          />
        </div>

        <div className="sv-container sv-hero relative">
          <p className="text-xs font-semibold tracking-[0.18em] text-white/80">
            {t.eyebrow}
          </p>

          <span
            aria-hidden="true"
            className="sv-accent block h-1 w-20 rounded-full bg-[#F72585]"
          />

          <h1
            className="sv-hero-title max-w-5xl font-black uppercase tracking-tight"
            style={DISPLAY_FONT}
          >
            <span className="block">{t.title}</span>
            <span className="mt-2 block">{t.accent}</span>
          </h1>

          <p className="sv-copy sv-spaced max-w-2xl text-white/90">
            {t.intro}
          </p>

          <nav aria-label={t.navigation} className="sv-navigation">
            <ul className="sv-nav-list flex flex-wrap">
              {categories.map((cat, index) => (
                <li key={cat.id}>
                  <a
                    href={`#${cat.id}`}
                    className="sv-nav-link inline-flex min-h-11 items-center gap-3 rounded-full border border-white/30 px-4 py-2 text-sm transition-colors hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
                  >
                    <span aria-hidden="true" className="text-xs text-white/70">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    {cat.nav}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </section>

      {/* CATEGORÍAS */}
      <div className="sv-container sv-categories">
        {categories.map((cat, index) => (
          <section
            key={cat.id}
            id={cat.id}
            aria-labelledby={`service-title-${cat.id}`}
            className="sv-category border-b border-[#081B3E]/15 last:border-b-0"
          >
            <div className="sv-category-header grid">
              <div className="min-w-0">
                <div className="flex items-center gap-4">
                  <span
                    aria-hidden="true"
                    className="sv-category-number flex shrink-0 items-center justify-center rounded-full bg-[#1648EE] text-xs font-bold tabular-nums text-white"
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#1648EE]">
                    {cat.eyebrow}
                  </p>
                </div>

                <h2
                  id={`service-title-${cat.id}`}
                  className="sv-title font-black uppercase tracking-tight"
                  style={DISPLAY_FONT}
                >
                  {cat.title}
                </h2>
              </div>

              <p className="sv-copy max-w-xl text-[#081B3E]/75">
                {cat.desc}
              </p>
            </div>

            {cat.plans && (
              <div className="sv-plans grid gap-4 sm:grid-cols-2">
                {cat.plans.map((plan) => (
                  <div
                    key={plan.id}
                    className="sv-panel relative min-w-0 overflow-hidden rounded-2xl bg-[#1648EE] text-white"
                  >
                    <span
                      aria-hidden="true"
                      className="absolute right-6 top-6 h-3 w-3 rounded-full bg-[#F72585]"
                    />

                    <h3 className="pr-6 text-sm font-bold uppercase tracking-wider">
                      {plan.name}
                    </h3>

                    <p className="sv-spaced flex flex-wrap items-baseline gap-3">
                      <span className="sv-price font-black tracking-tight">
                        {plan.price}
                      </span>
                      <span className="text-sm text-white/85">{t.monthly}</span>
                    </p>

                    <p className="sv-small-copy mt-3 text-white/90">
                      {plan.desc}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <ul className="sv-service-grid grid">
              {cat.items.map(([title, description], itemIndex) => (
                <li
                  key={`${cat.id}-${itemIndex}`}
                  className="sv-panel min-w-0 rounded-2xl border border-[#081B3E]/10 bg-white"
                >
                  <div aria-hidden="true" className="sv-card-rule flex items-center gap-3">
                    <span className="h-2 w-2 rounded-full bg-[#F72585]" />
                    <span className="h-px flex-1 bg-[#081B3E]/15" />
                  </div>

                  <h3 className="sv-card-title font-bold">{title}</h3>

                  <p className="sv-small-copy mt-3 text-[#081B3E]/75">
                    {description}
                  </p>
                </li>
              ))}
            </ul>

            {cat.benefits && (
              <div className="sv-panel sv-benefits rounded-2xl bg-[#081B3E] text-[#F7F3E9]">
                <h3 className="text-xs font-bold uppercase tracking-[0.16em]">
                  {t.benefits}
                </h3>

                <ul className="sv-benefits-list grid sm:grid-cols-2">
                  {cat.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-3">
                      <Check
                        aria-hidden="true"
                        className="mt-0.5 h-5 w-5 shrink-0 text-[#F72585]"
                      />
                      <span className="text-sm leading-6">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {cat.note && (
              <p className="sv-note max-w-4xl text-xs text-[#081B3E]/70">
                {cat.note}
              </p>
            )}

            <button
              type="button"
              onClick={() => setModalType(cat.id)}
              className={`${PRIMARY_BUTTON} sv-spaced`}
              aria-haspopup="dialog"
            >
              {cat.cta}
              <ArrowUpRight aria-hidden="true" className="h-5 w-5 shrink-0" />
            </button>
          </section>
        ))}
      </div>

      {selectedCategory && (
        <ServiceModal
          formType={selectedCategory.id}
          title={selectedCategory.cta}
          language={language}
          onClose={() => setModalType(null)}
        />
      )}
    </div>
  );
}

/* ======================================================
   MODAL NATIVO
   - Fondo inerte y navegación de teclado contenida.
   - Escape y clic exterior.
   - El primer control es Cerrar, no un campo de texto.
   - Sin autoFocus ni llamadas a input.focus().
====================================================== */

function ServiceModal({ formType, title, language, onClose }) {
  const dialogRef = useRef(null);
  const outsidePointerRef = useRef(false);
  const titleId = useId();
  const [busy, setBusy] = useState(false);
  const t = UI[language];

  useEffect(() => {
    const dialog = dialogRef.current;
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    if (dialog && !dialog.open) {
      dialog.showModal();
    }

    // El teclado puede reducir el viewport visual sin cambiar 100dvh.
    // Ajustamos sólo el tamaño/posición del diálogo, sin enfocar inputs.
    const viewport = window.visualViewport;
    function syncViewport() {
      const height = viewport?.height || window.innerHeight;
      const top = viewport?.offsetTop || 0;
      if (!dialog || !Number.isFinite(height) || height <= 0) return;

      dialog.style.setProperty("--sv-viewport-height", `${height}px`);
      dialog.style.setProperty("--sv-viewport-top", `${Math.max(0, top)}px`);
    }

    syncViewport();
    window.addEventListener("resize", syncViewport, { passive: true });
    viewport?.addEventListener("resize", syncViewport, { passive: true });
    viewport?.addEventListener("scroll", syncViewport, { passive: true });

    return () => {
      window.removeEventListener("resize", syncViewport);
      viewport?.removeEventListener("resize", syncViewport);
      viewport?.removeEventListener("scroll", syncViewport);
      if (dialog?.open) dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  function requestClose() {
    if (!busy) onClose();
  }

  function isOutside(event) {
    const dialog = dialogRef.current;
    if (!dialog || event.target !== dialog) return false;

    const rect = dialog.getBoundingClientRect();

    return (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    );
  }

  return (
    <dialog
      ref={dialogRef}
      lang={language}
      aria-labelledby={titleId}
      className="sv-modal overflow-hidden rounded-3xl border-0 bg-[#F7F3E9] p-0 text-[#081B3E] shadow-2xl backdrop:bg-[#081B3E]/75"
      onCancel={(event) => {
        event.preventDefault();
        requestClose();
      }}
      onPointerDown={(event) => {
        outsidePointerRef.current = isOutside(event);
      }}
      onPointerCancel={() => {
        outsidePointerRef.current = false;
      }}
      onClick={(event) => {
        const dismiss = outsidePointerRef.current && isOutside(event);
        outsidePointerRef.current = false;

        if (dismiss) requestClose();
      }}
    >
      <div
        className="sv-modal-shell flex min-h-0 flex-col"
      >
        <header className="sv-modal-header flex shrink-0 items-center border-b border-[#081B3E]/15">
          <span
            aria-hidden="true"
            className="h-3 w-3 shrink-0 rounded-full bg-[#F72585]"
          />

          <h2 id={titleId} className="sv-modal-title min-w-0 flex-1 font-bold">
            {title}
          </h2>

          <button
            type="button"
            onClick={requestClose}
            disabled={busy}
            aria-label={t.close}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#081B3E]/15 transition-colors hover:bg-[#081B3E]/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1648EE] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </header>

        <div className="sv-modal-body min-h-0 overflow-y-auto overscroll-contain">
          <ServiceForm
            formType={formType}
            language={language}
            onBusyChange={setBusy}
          />
        </div>
      </div>
    </dialog>
  );
}

/* ======================================================
   FORMULARIO
====================================================== */

function ServiceForm({ formType, language, onBusyChange }) {
  const t = UI[language];

  const [step, setStep] = useState(0);
  const [form, setForm] = useState(createEmptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [saveError, setSaveError] = useState(false);

  // Protección inmediata contra doble envío.
  const savingRef = useRef(false);
  const mountedRef = useRef(false);
  const formId = useId();

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const options = {
    ...t.options,
    idioma: LANGUAGE_OPTIONS,
    sucursales: ["1", "2", "3", "4+"].map((value) => [value, value]),
  };

  function updateField(field, value) {
    if (savingRef.current) return;

    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setSaveError(false);
  }

  function goBack() {
    if (savingRef.current) return;

    setErrors({});
    setSaveError(false);
    setStep((current) => Math.max(0, current - 1));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (savingRef.current || submitted) return;

    if (step < 3) {
      const nextErrors = validateStep(form, formType, step);
      setErrors(nextErrors);

      if (Object.keys(nextErrors).length === 0) {
        setStep((current) => current + 1);
      }

      return;
    }

    // Validación completa antes de escribir en Firestore.
    for (let index = 0; index < 3; index += 1) {
      const nextErrors = validateStep(form, formType, index);

      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors);
        setStep(index);
        return;
      }
    }

    savingRef.current = true;
    setSaving(true);
    setSaveError(false);
    onBusyChange(true);

    const cleanForm = Object.fromEntries(
      Object.entries(form).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() : value,
      ])
    );

    try {
      // Se conserva la colección y el esquema original.
      await addDoc(collection(db, "leads_servicios"), {
        formType,
        ...cleanForm,
        createdAt: serverTimestamp(),
        page: window.location.pathname,
        utm: getUtmParams(),
      });

      if (mountedRef.current) setSubmitted(true);
    } catch {
      if (mountedRef.current) setSaveError(true);
    } finally {
      savingRef.current = false;

      if (mountedRef.current) {
        setSaving(false);
        onBusyChange(false);
      }
    }
  }

  function renderField(field) {
    const error = errors[field] ? t.errors[errors[field]] : undefined;
    const label = t.labels[field];

    if (options[field]) {
      return (
        <ChoiceGroup
          key={field}
          label={
            field === "sucursales"
              ? `${label} (${t.optional})`
              : label
          }
          options={options[field]}
          value={form[field]}
          onChange={(value) => updateField(field, value)}
          error={error}
          required={field !== "sucursales"}
          nativeLanguages={field === "idioma"}
        />
      );
    }

    if (field === "descripcion") {
      return (
        <TextField
          key={field}
          name={field}
          label={t.descriptionLabels[formType]}
          placeholder={t.descriptionPlaceholders[formType]}
          value={form[field]}
          onChange={(value) => updateField(field, value)}
          error={error}
          multiline
        />
      );
    }

    const attributes = {
      nombre: { autoComplete: "given-name" },
      apellidos: { autoComplete: "family-name" },
      pais: { autoComplete: "country-name" },
      telefono: {
        type: "tel",
        inputMode: "tel",
        autoComplete: "tel",
        placeholder: t.phonePlaceholder,
      },
      correo: {
        type: "email",
        inputMode: "email",
        autoComplete: "email",
        placeholder: t.emailPlaceholder,
      },
    };

    return (
      <TextField
        key={field}
        name={field}
        label={label}
        value={form[field]}
        onChange={(value) => updateField(field, value)}
        error={error}
        {...attributes[field]}
      />
    );
  }

  if (submitted) {
    return (
      <div role="status" className="sv-success text-center">
        <span
          aria-hidden="true"
          className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#1648EE] text-white"
        >
          <Check className="h-8 w-8" />
        </span>

        <h3 className="mt-6 text-2xl font-bold">{t.successTitle}</h3>

        <p className="mx-auto mt-4 max-w-sm text-sm leading-7 text-[#081B3E]/75">
          {t.successBody.replace("{name}", form.nombre.trim())}
        </p>
      </div>
    );
  }

  const question =
    step === 0
      ? t.questions.name
      : step === 1
      ? t.questions[formType]
      : step === 2
      ? t.questions.contact
      : t.questions.review;

  const summaryFields = [
    "nombre",
    "apellidos",
    ...CONTEXT_FIELDS[formType],
    "telefono",
    "whatsapp",
    "correo",
  ].filter((field) => form[field] !== "" && form[field] !== null);

  function summaryValue(field) {
    if (options[field]) {
      return (
        options[field].find(([value]) => value === form[field])?.[1] ||
        String(form[field])
      );
    }

    return form[field].trim();
  }

  return (
    <form
      className="sv-form"
      noValidate
      onSubmit={handleSubmit}
      aria-busy={saving}
      aria-labelledby={`${formId}-question`}
    >
      <div className="sv-progress">
        <div
          role="status"
          className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-[#081B3E]/70"
        >
          <span className="font-bold">{t.steps[step]}</span>
          <span>
            {t.step} {step + 1} {t.of} 4
          </span>
        </div>

        <div aria-hidden="true" className="flex gap-2">
          {[0, 1, 2, 3].map((index) => (
            <span
              key={index}
              className={`h-1.5 flex-1 rounded-full ${
                index <= step ? "bg-[#1648EE]" : "bg-[#081B3E]/10"
              }`}
            />
          ))}
        </div>
      </div>

      <h3
        id={`${formId}-question`}
        className="sv-question font-bold tracking-tight"
      >
        {question}
      </h3>

      <fieldset disabled={saving} className="sv-fields min-w-0">
        <legend className="sr-only">{question}</legend>

        {step < 3 ? (
          <div
            className={
              step === 0
                ? "sv-name-grid"
                : "sv-field-stack"
            }
          >
            {fieldsForStep(formType, step).map(renderField)}
          </div>
        ) : (
          <>
            <dl className="divide-y divide-[#081B3E]/10 overflow-hidden rounded-2xl border border-[#081B3E]/15 bg-white">
              {summaryFields.map((field) => (
                <div
                  key={field}
                  className="sv-review-row grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] sm:gap-5"
                >
                  <dt className="text-xs font-bold text-[#081B3E]/65">
                    {t.labels[field]}
                  </dt>

                  <dd className="sv-review-value min-w-0 whitespace-pre-wrap break-words text-sm leading-6 sm:text-right">
                    {summaryValue(field)}
                  </dd>
                </div>
              ))}
            </dl>

            <p className="mt-5 text-xs leading-6 text-[#081B3E]/70">
              {t.reviewNote}
            </p>
          </>
        )}

        {saveError && (
          <p
            role="alert"
            className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm leading-6 text-red-800"
          >
            {t.errors.save}
          </p>
        )}

        <div className="sv-form-actions flex flex-wrap items-center justify-between gap-3 border-t border-[#081B3E]/15">
          {step > 0 ? (
            <button
              type="button"
              onClick={goBack}
              disabled={saving}
              className="inline-flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1648EE] disabled:opacity-40"
            >
              <ArrowLeft aria-hidden="true" className="h-4 w-4" />
              {t.back}
            </button>
          ) : (
            <span />
          )}

          <button
            type="submit"
            disabled={saving}
            className={PRIMARY_BUTTON}
          >
            {saving ? (
              <>
                <Loader2
                  aria-hidden="true"
                  className="h-4 w-4 animate-spin motion-reduce:animate-none"
                />
                {t.sending}
              </>
            ) : (
              <>
                {step === 3 ? t.send : t.next}
                <ArrowUpRight aria-hidden="true" className="h-4 w-4" />
              </>
            )}
          </button>
        </div>
      </fieldset>
    </form>
  );
}

/* ======================================================
   CAMPOS ACCESIBLES
====================================================== */

function TextField({
  name,
  label,
  value,
  onChange,
  error,
  multiline = false,
  type = "text",
  inputMode,
  autoComplete,
  placeholder,
}) {
  const id = useId();
  const errorId = `${id}-error`;

  const sharedProps = {
    id,
    name,
    value,
    placeholder,
    required: true,
    "aria-invalid": Boolean(error),
    "aria-describedby": error ? errorId : undefined,
    onChange: (event) => onChange(event.target.value),
    className: `${FIELD_CLASS} ${
      error ? "border-red-500" : "border-[#081B3E]/20"
    }`,
  };

  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-2 block text-sm font-semibold">
        {label}
      </label>

      {multiline ? (
        <textarea
          {...sharedProps}
          rows={4}
          className={`${sharedProps.className} resize-y`}
        />
      ) : (
        <input
          {...sharedProps}
          type={type}
          inputMode={inputMode}
          autoComplete={autoComplete}
        />
      )}

      {error && (
        <p id={errorId} role="alert" className="mt-2 text-xs leading-5 text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}

function ChoiceGroup({
  label,
  options,
  value,
  onChange,
  error,
  required = true,
  nativeLanguages = false,
}) {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <fieldset
      className="min-w-0"
      aria-describedby={error ? errorId : undefined}
    >
      <legend className="mb-3 text-sm font-semibold">{label}</legend>

      <div className="sv-choice-grid">
        {options.map(([optionValue, optionLabel, description], index) => {
          const optionId = `${id}-${index}`;

          return (
            <label
              key={String(optionValue)}
              htmlFor={optionId}
              className="relative block min-w-0 cursor-pointer"
            >
              <input
                id={optionId}
                name={id}
                type="radio"
                value={String(optionValue)}
                checked={value === optionValue}
                onChange={() => onChange(optionValue)}
                required={required}
                aria-invalid={Boolean(error)}
                aria-describedby={error ? errorId : undefined}
                className="peer sr-only"
              />

              <span
                className="sv-choice-card flex items-start rounded-xl border border-[#081B3E]/20 bg-white transition-colors peer-checked:border-[#1648EE] peer-checked:bg-[#1648EE]/5 peer-focus-visible:ring-2 peer-focus-visible:ring-[#1648EE] peer-focus-visible:ring-offset-2 peer-disabled:cursor-not-allowed peer-disabled:opacity-60"
              >
                <span
                  aria-hidden="true"
                  className={`mt-0.5 flex h-4 w-4 shrink-0 items-center
                              justify-center rounded-full border ${
                    value === optionValue
                      ? "border-[#1648EE] bg-[#1648EE]"
                      : "border-[#081B3E]/30"
                  }`}
                >
                  {value === optionValue && (
                    <span className="h-1.5 w-1.5 rounded-full bg-white" />
                  )}
                </span>

                <span className="sv-choice-text min-w-0">
                  <span
                    lang={nativeLanguages ? optionValue : undefined}
                    className="block text-sm font-bold leading-5"
                  >
                    {optionLabel}
                  </span>

                  {description && (
                    <span className="mt-1 block text-xs leading-5 text-[#081B3E]/70">
                      {description}
                    </span>
                  )}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      {error && (
        <p id={errorId} role="alert" className="mt-2 text-xs leading-5 text-red-700">
          {error}
        </p>
      )}
    </fieldset>
  );
}
